import { buildPanelShape } from '@toolproof-core/visualization/_pure/shapes';
import {
    defineEntityConstellationDerivation,
    defineEntityItemDerivation,
    toEntityItemCollectionDerivation,
} from '@toolproof-core/visualization/derivation/_pure/derivation-values';
import type { Vector3Tuple } from '@toolproof-core/visualization/_contract/geometry';
import type { EntityConstellationParametricState } from '@toolproof-core/visualization/_contract/constellation';
import type { SpaceDerivationFrame, TransitionPhase } from '@toolproof-core/visualization/derivation/_contract/derivation';

import { getResourceTypeRingRadius } from '@/workflow/_pure/config/derivationConfig';
import { WORKFLOW_KEYS } from '@/workflow/_pure/keyCodec';
import { MOCK_RESOURCE_TYPES } from '@/workflow-runner/conceptResources';
import { compositeFlagLabels } from '@/workflow-runner/conceptCompositeFlag';
import type { Config } from '@/workflow/_contract/config';

export const PRODUCT_ITEM_KEY = 'item:entity:Product:Natural-Boolean:8:false';
const PRODUCT_COLLECTION_KEY = 'collection:entity:Product:Natural-Boolean';
const PRODUCT_CONSTELLATION_KEY = 'constellation:entity:Product:Natural-Boolean';
type Composition = 'product' | 'sum';
export const SUM_ITEM_KEY = 'item:entity:Sum:Natural-Boolean';
const COMPONENTS = [
    { handle: 'TYPE-Natural', value: '8' },
    { handle: 'TYPE-Boolean', value: 'false' },
] as const;
const PRODUCT_ANGLE = Math.PI + Math.PI / 6;

export function productGeometry(config: Config) {
    const ringRadius = getResourceTypeRingRadius(config);
    const sphereRadius = config.domainPresentation.entities.resourceTypes.item?.shape?.radius;
    if (sphereRadius === undefined || sphereRadius <= 0 || sphereRadius >= ringRadius) {
        throw new Error('Concept product requires ResourceType spheres smaller than their ring.');
    }
    const panel = config.domainPresentation.entities.workflowStateEntries.item.shape;
    const halfAngle = Math.asin(sphereRadius / ringRadius);
    const resourceRadius = Math.max(ringRadius + sphereRadius + panel.height * 3,
        panel.width * 1.5 / (2 * halfAngle));
    return { ringRadius, resourceRadius, halfAngle, panel };
}

export function productAngles(config: Config, progress = 1): [number, number] {
    const { halfAngle } = productGeometry(config);
    return [
        Math.PI + (Math.PI / 6 - halfAngle) * progress,
        Math.PI + Math.PI / 3 - (Math.PI / 6 - halfAngle) * progress,
    ];
}

export function productCompositeLabels(config: Config, composition: Composition, progress = 1) {
    const { ringRadius, halfAngle } = productGeometry(config);
    const angles = productAngles(config, progress);
    return compositeFlagLabels({
        config, sphereRadius: ringRadius * Math.sin(halfAngle),
        text: composition === 'product' ? 'Natural x Boolean' : 'Natural + Boolean',
        components: COMPONENTS.map(({ handle }, index) => ({
            itemKey: `item:entity:${handle}`,
            center: [ringRadius * Math.cos(angles[index]), 0, ringRadius * Math.sin(angles[index])],
        })),
    });
}

export function withProductConfig(config: Config): Config {
    const angles = productAngles(config);
    return { ...config, domainPresentation: { ...config.domainPresentation,
        entities: { ...config.domainPresentation.entities,
            resourceTypes: { ...config.domainPresentation.entities.resourceTypes,
                angularPlacement: { placementKind: 'explicit', angleByResourceTypeHandle: {
                    'TYPE-Natural': angles[0], 'TYPE-Boolean': angles[1],
                    ...Object.fromEntries(MOCK_RESOURCE_TYPES.map((type, index) =>
                        [type.resourceTypeHandle, Math.PI + (index + 2) * Math.PI / 3])),
                } } } } } };
}

export function deriveCompositeCollections(config: Config, composition: Composition = 'product', alternative: 0 | 1 = 0) {
    const sum = composition === 'sum';
    const { resourceRadius, halfAngle, panel } = productGeometry(config);
    const itemConfig = config.domainPresentation.entities.workflowStateEntries.item;
    const curvedShape = buildPanelShape({
        width: panel.width, height: panel.height, depth: panel.depth, curvatureRadius: resourceRadius,
    });
    const components = COMPONENTS.map(({ handle, value }, index) => toEntityItemCollectionDerivation({
        itemCollectionKey: `collection:entity:Resource:${handle}`,
        itemDerivations: [defineEntityItemDerivation({
            item: { itemKind: 'entity', itemKey: `item:entity:Resource:${handle}:${value}`, name: value,
                description: `Resource instance of ${handle}: ${value}` },
            parametricState: { shape: { ...curvedShape, appearanceMode: sum && index !== alternative ? 'outlined' : 'filled' },
                visualStyle: config.domainPresentation.entities.resourceTypes.item?.visualStyle,
                label: { onItem: { ...itemConfig.label?.onItem, text: value } } },
        })],
    }));
    return [...components, toEntityItemCollectionDerivation({
        itemCollectionKey: sum ? 'collection:entity:Sum:Natural-Boolean' : PRODUCT_COLLECTION_KEY,
        itemDerivations: [defineEntityItemDerivation({
            item: { itemKind: 'entity', itemKey: sum ? SUM_ITEM_KEY : PRODUCT_ITEM_KEY,
                name: sum ? 'Natural + Boolean' : '(8, false)',
                description: sum ? 'One sum Resource: either a Natural or a Boolean, never both'
                    : 'One product Resource with Natural and Boolean components' },
            parametricState: {
                shape: { ...buildPanelShape({
                    width: 2 * resourceRadius * halfAngle + panel.width + panel.height * 0.5,
                    height: panel.height * 1.5, curvatureRadius: resourceRadius,
                }), appearanceMode: 'outlined' },
                visualStyle: { color: config.domainPresentation.entities.resourceTypes.item?.visualStyle?.color, opacity: 1 },
            },
        })],
    })];
}

function spokeState(config: Config, angle: number, handle: string): EntityConstellationParametricState {
    const { ringRadius, resourceRadius, panel } = productGeometry(config);
    return { layout: { layoutKind: 'spoke',
        anchor: { anchorKind: 'parametric', parametricKind: 'ring-intersection',
            ringItemKey: `item:entity:${handle}`,
            anglePolicy: { anglePolicyKind: 'explicit', angle },
            radialOffset: resourceRadius - ringRadius },
        direction: [Math.cos(angle), 0, Math.sin(angle)], spacing: panel.height * 3 } };
}

export function deriveCompositeConstellations(config: Config, composition: Composition = 'product') {
    const sum = composition === 'sum';
    const angles = productAngles(config);
    return [
        ...COMPONENTS.map(({ handle, value }, index) => defineEntityConstellationDerivation({
            constellation: { constellationKind: 'entity', constellationKey: `constellation:entity:Resource:${handle}`,
                itemCollectionKey: `collection:entity:Resource:${handle}`,
                anchor: { itemKey: `item:entity:Resource:${handle}:${value}`, anchorKey: 'center' } },
            parametricState: spokeState(config, angles[index], handle),
        })),
        defineEntityConstellationDerivation({
            constellation: { constellationKind: 'entity',
                constellationKey: sum ? 'constellation:entity:Sum:Natural-Boolean' : PRODUCT_CONSTELLATION_KEY,
                itemCollectionKey: sum ? 'collection:entity:Sum:Natural-Boolean' : PRODUCT_COLLECTION_KEY,
                anchor: { itemKey: sum ? SUM_ITEM_KEY : PRODUCT_ITEM_KEY, anchorKey: 'center' } },
            parametricState: spokeState(config, PRODUCT_ANGLE, 'TYPE-Natural'),
        }),
    ];
}

export function productCameraTarget(config: Config): Vector3Tuple {
    const { ringRadius } = productGeometry(config);
    return [ringRadius * Math.cos(PRODUCT_ANGLE), 0, ringRadius * Math.sin(PRODUCT_ANGLE)];
}

export function deriveProductTransitions(frame: SpaceDerivationFrame, config: Config, revision: number): TransitionPhase[] {
    const ring = frame.constellationParametricState?.entityConstellationByKey?.[WORKFLOW_KEYS.constellations.resourceType]?.layout;
    if (ring?.layoutKind !== 'ring' || ring.angularPlacement?.placementKind !== 'explicit') {
        throw new Error('Concept product animation requires explicit ResourceType angles.');
    }
    const placement = ring.angularPlacement;
    const phases: TransitionPhase[] = Array.from({ length: 49 }, (_, index) => {
        const t = index / 48;
        const progress = t * t * (3 - 2 * t);
        const angles = productAngles(config, progress);
        const labels = productCompositeLabels(config, 'product', progress);
        return {
            transitionOptions: { transitionKind: 'instant', delay: index === 0 ? 0 : 25,
                transitionKey: `concept-product:${revision}:${index}` },
            patch: {
                entityItemLabelChannelsToRemoveByKey: { 'item:entity:TYPE-Boolean': ['aboveItem'] },
                itemParametricState: { entityItemByKey: {
                    [PRODUCT_ITEM_KEY]: { visualStyle: { opacity: 0 } },
                    ...Object.fromEntries(labels.map(({ itemKey, state }) => [itemKey, state])),
                } },
                constellationParametricState: { entityConstellationByKey: {
                    [WORKFLOW_KEYS.constellations.resourceType]: { layout: { ...ring,
                        angularPlacement: { ...placement, angleByItemKey: { ...placement.angleByItemKey,
                            'item:entity:TYPE-Natural': angles[0], 'item:entity:TYPE-Boolean': angles[1] } } } },
                    ...Object.fromEntries(COMPONENTS.map(({ handle }, component) =>
                        [`constellation:entity:Resource:${handle}`, spokeState(config, angles[component], handle)])),
                } },
            },
        };
    });
    phases.push({ transitionOptions: { transitionKind: 'interpolated', duration: 400,
        transitionKey: `concept-product:${revision}:frame` },
        patch: { itemParametricState: { entityItemByKey: { [PRODUCT_ITEM_KEY]: { visualStyle: { opacity: 1 } } } } } });
    return phases;
}
